import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { CdsTabsModule } from '@celerofinancas/design-system';
import { LoadUserAction } from '@celerofinancas/state-management';
import {
  BfmComparativeComponent,
  BfmMonthlyComponent,
  COMPARATIVE_REPORT_HEADER_CONFIG,
  COMPARATIVE_REPORT_HEADER_DEFAULT,
  COMPARATIVE_REPORT_LIST_CONFIG,
  COMPARATIVE_REPORT_LIST_DEFAULT_CONFIG,
  COMPARATIVE_REPORT_TABLE_CONFIG,
  COMPARATIVE_REPORT_TABLE_DEFAULT_CONFIG,
  RESULTS_COA_VALUE_CONFIG,
  RESULT_COA_VALUE_DEFAULT_CONFIG,
} from '@celerofinancas/ui-reports';
import { OnInit, inject } from '@angular/core';
import { FailureWrapperComponent } from '@shared/failure-wrapper/failure-wrapper.component';
import { TokenExchange } from '@shared/models/token-exchange';

/**
 * Screen tabs, in the order they appear.
 */
type Tab = 'monthly' | 'comparative';

/**
 * Reports screen: serves two cards on a single page, one tab each —
 * [86e2v9301](https://app.clickup.com/t/86e2v9301) (DFM) and
 * [86e2v92q5](https://app.clickup.com/t/86e2v92q5) (comparative results).
 *
 * They share a screen because that is the format of `serasa-cl-webcomponents`
 * (`src/app/pages/reports`), the template the cards ask us to follow. Both
 * components come whole from `@celerofinancas/ui-reports` — nothing is
 * reimplemented here, only configured, with the GetNet design system applied on
 * top through `--cds-*` tokens.
 *
 * ## What it consumes from the mock
 *
 * - DFM → `GET /v2/reports/monthly/get/`
 * - Comparative → `GET /v2/reports/comparative_results/get/`
 *
 * Both come from the same income-statement aggregation in the mock
 * (`pnl-aggregation.ts`), so opening the two tabs and comparing the same month
 * gives the same number.
 *
 * ## Download turned off
 *
 * `showButtonDownloadCSV: false` because the Downloads module was not contracted
 * by GetNet — the same restriction the statement card
 * ([86e2v9afw](https://app.clickup.com/t/86e2v9afw), rule 5) states. Leaving the
 * button visible would offer the client a feature they do not have.
 *
 * ## Why the empty-state copy comes from a provider
 *
 * `bfm-monthly-comparative` declares a `noContentDescriptions` **input**, and
 * binding it is what `serasa-cl-webcomponents` does — but the binding is dead:
 * the component's `ngOnInit` overwrites the property with
 * `configListColumns.noContentDescriptionsTitle/Subtitle`, read from
 * `COMPARATIVE_REPORT_LIST_CONFIG`. Whatever the parent passes is discarded on
 * the first change-detection pass. Verified in the browser: the instance held
 * the package default, not the bound object.
 *
 * So the copy goes through the token. `indicators` is required by
 * `ComparativeReportListConfig` and drives the table's fixed column, hence the
 * spread over the default — providing only the two strings would empty the
 * "Indicadores" column.
 *
 * ## Why it dispatches LoadUserAction
 *
 * Same reason as the dashboard: the package components self-gate on the
 * `company_pk` in `CompanyState`, which is only hydrated by
 * `CoreRegistryInterceptor` reacting to an HTTP request. Without the dispatch
 * nobody asks and the screen stays blank. See the `DashboardComponent` docblock
 * for the full reasoning.
 */
@Component({
  selector: 'reports',
  standalone: true,
  imports: [CdsTabsModule, BfmMonthlyComponent, BfmComparativeComponent,
    FailureWrapperComponent,
  ],
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss', './reports.overrides.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: COMPARATIVE_REPORT_HEADER_CONFIG,
      useValue: { ...COMPARATIVE_REPORT_HEADER_DEFAULT, showButtonDownloadCSV: false },
    },
  /**
   * Configuração do gráfico e da tabela da aba "DFM por Categorias".
   *
   * O token é **obrigatório** para a ação "Ver lançamentos" funcionar, e a ausência dele
   * não estoura: o componente é criado e renderiza em branco. Ao abrir uma categoria, o
   * pacote instancia a lista e define as colunas com
   * `this.configs.financialRecordColumnList ?? []` — sem o token a expressão cai em lista
   * vazia, a lista monta sem coluna nenhuma e o clique parece não fazer nada. Foi o item 6
   * do doc de considerações, reportado como "o clique não abre".
   *
   * Espalha o default do pacote em vez de reescrever as colunas: elas já estão certas
   * (valor, pago em, nome, status, banco, método), e duplicá-las aqui só criaria uma
   * segunda fonte para divergir da primeira.
   */
    {
      provide: RESULTS_COA_VALUE_CONFIG,
      useValue: { ...RESULT_COA_VALUE_DEFAULT_CONFIG },
    },
    {
      provide: COMPARATIVE_REPORT_LIST_CONFIG,
      useValue: {
        ...COMPARATIVE_REPORT_LIST_DEFAULT_CONFIG,
        // Item 8 do doc de considerações: copy nova do estado vazio, definida pelo PD.
        // A ilustração vem do mesmo asset do DFM (`assets/common/light/shared/no-content.svg`),
        // reaproveitado e não duplicado, como o item pede.
        noContentDescriptionsTitle: 'Como quer comparar seus resultados?',
        noContentDescriptionsSubtitle:
          'Selecione a visão que preferir nos filtros acima para acompanhar a evolução do seu negócio.',
      },
    },
    {
      /**
       * Item 9 do doc de relatórios ([86e33facd](https://app.clickup.com/t/86e33facd)):
       * "Saldo Inicial" e "Saldo Final" dizem o sinal — verde acima de zero, vermelho
       * abaixo. O rótulo da linha continua neutro.
       *
       * Todas as outras linhas do comparativo já têm cor pela classe que o relatório dá
       * a elas; as de saldo carregam `sum`, que as pinta de neutro. A opção que existia
       * antes (`useDirectiveColor`) entregaria a **tabela inteira** à diretiva e tiraria
       * a classe de todas as células, deixando as linhas de resultado sem cor — por isso
       * o `ui-reports` 6.4.0 ganhou esta, estreita
       * ([86e33facd](https://app.clickup.com/t/86e33facd), PR #199 do pacote), e é ela
       * que se liga aqui.
       *
       * O espalhamento do default é obrigatório: o componente lê o token com `??`, e não
       * com merge, então prover só o campo novo apagaria os outros.
       */
      provide: COMPARATIVE_REPORT_TABLE_CONFIG,
      useValue: { ...COMPARATIVE_REPORT_TABLE_DEFAULT_CONFIG, colorBalanceFromValue: true },
    },
  ],
})
export class ReportsComponent extends TokenExchange implements OnInit {
  /**
   * Signal holding the current active tab.
   */
  private readonly selectedTab = signal<Tab>('monthly');

  /**
   * Verify tab is selected.
   *
   * @param tab tab to compare against the active one
   * @returns whether the given tab is the active one
   */
  public isSelectedTab(tab: Tab): boolean {
    return this.selectedTab() === tab;
  }

  /**
   * Change selected tab.
   *
   * @param tab tab to activate
   */
  public setSelectedTab(tab: Tab): void {
    this.selectedTab.set(tab);
  }

  /**
   * Loads the user on init, for the same reason as the dashboard and the statement
   * — see `DashboardComponent`.
   */
  public ngOnInit(): void {
    this.store.dispatch(new LoadUserAction());
  }
}
